import { useEffect, useRef, useState } from 'react';
import { Button } from './ui';
import { api } from '../lib/api';
import type { DocumentPreview } from '../types';
import { canShareFile, shareFile, shareSheetAvailable } from '../utils/openDocument';

/**
 * THE IN-APP DOCUMENT VIEWER.
 *
 * Before this, clicking a document in the Deal Room opened a NEW BROWSER TAB at its signed URL.
 * That worked, and it threw the operator out of the deal he was reading - which is the whole
 * reason this exists. The tab route is kept as a deliberate escape hatch, not removed.
 *
 * ⭐ THE SERVER DECIDES THE SHAPE, THE VIEWER ONLY RENDERS IT. Dispatching on the filename here
 * would put a second opinion about file types in the browser, and `files.mime_type` is NULL on
 * 86% of rows so the obvious client-side check would have been wrong anyway.
 *
 * ⛔ HTML IS RENDERED IN A SANDBOXED IFRAME, VIA `srcdoc`. A brochure is our own server-rendered
 * markup, and a converted .docx is derived from a file that may have come from ANYONE - a client
 * emailing a Word document is exactly the case mammoth was added for. `sandbox` with no
 * allow-list runs no scripts, submits no forms and gets no same-origin access, so images and CSS
 * still render and nothing in a document can act.
 *
 * ⭐⭐ MOB4 (the 128th): IT OPENS A PROPERTY'S DOCUMENT TOO. His phone report - a filed Word file
 * from a property's documents opened a white screen - was this viewer missing from the page he was
 * on: the property panel had only the file's own link, and an iPhone shows a Word file in a window
 * as a white page. `property` names the record instead of `dealId`; the server gates each its own way.
 *
 * ⭐⭐ ON A PHONE THE FILE LEAVES THROUGH THE SHARE SHEET, NEVER A WINDOW (measured on the iPhone
 * simulator as a home-screen app - `utils/openDocument.ts`). The sheet opens only inside a tap, so
 * the bytes are fetched when the viewer opens; until they arrive the button WAITS rather than fall
 * back to the window that strands him. A PDF is the exception: iOS's own sheet shows one, so it
 * keeps Download and nothing is fetched ahead.
 */
type Source =
  | { dealId: string; property?: undefined }
  | { property: { kind: 'deal' | 'master'; id: string }; dealId?: undefined };

type Props = Source & {
  docId: string;
  title: string;
  onClose: () => void;
  onDownload: () => void;
  /** The document's own signed link, for the share sheet. Without it the viewer only has Download. */
  fileUrl?: () => Promise<string>;
  /**
   * LOI-PDF1b (the 130th): WHY the page opened this here instead of what he asked for - "The PDF could not be made
   * from this letter. Showing the Word file instead." ⭐ FOUND BY LOOKING: the page printed that sentence beside its
   * list, BEHIND this dialog, so he saw the wrong document and no reason until he closed it. Said here, at the top.
   */
  notice?: string | null;
};

export default function DocumentViewer({
  dealId, property, docId, title, onClose, onDownload, fileUrl, notice,
}: Props) {
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const propertyKind = property?.kind;
  const propertyId = property?.id;

  /**
   * The share sheet's file. `sheet` is decided ONCE, when the viewer opens: a touch screen that can
   * share files and a caller that can sign the link. `ready` then moves waiting -> the file, or
   * waiting -> failed, and only `failed` gives Download back.
   */
  const [sheet] = useState(() => !!fileUrl && shareSheetAvailable());
  const [shareable, setShareable] = useState<File | null>(null);
  const [fileFailed, setFileFailed] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  // A ref, not a dependency: callers pass an inline arrow, which is a new function every render.
  const fileUrlRef = useRef(fileUrl);
  fileUrlRef.current = fileUrl;

  useEffect(() => {
    let cancelled = false;
    // async/await with try/catch rather than a .then/.catch chain: the handler is attached in
    // the same synchronous frame as the call, so a request that fails after this component has
    // already unmounted still settles into a handled path.
    const load = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = propertyKind && propertyId
          ? await api.previewPropertyDocument(propertyKind, propertyId, docId)
          : await api.previewDealRoomDocument(dealId as string, docId);
        if (!cancelled) setPreview(res.preview);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not open this document');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [dealId, propertyKind, propertyId, docId]);

  // The bytes, fetched AHEAD of the tap that shares them. Named from the preview: the server's
  // filename is the one he will see in Files, Mail or Messages.
  const previewKind = preview?.kind;
  const previewFilename = preview?.filename;
  useEffect(() => {
    if (!sheet || !previewFilename || previewKind === 'pdf') return;
    let cancelled = false;
    const fetchFile = async (): Promise<void> => {
      try {
        const url = await (fileUrlRef.current as () => Promise<string>)();
        const res = await fetch(url);
        if (!res.ok) throw new Error(`The file answered ${res.status}`);
        const blob = await res.blob();
        const file = new File([blob], previewFilename, { type: blob.type });
        if (!canShareFile(file)) throw new Error('This phone will not share that file');
        if (!cancelled) setShareable(file);
      } catch {
        // ⛔ A way out is never taken away: without the file, Download comes back.
        if (!cancelled) setFileFailed(true);
      }
    };
    void fetchFile();
    return () => { cancelled = true; };
  }, [sheet, previewFilename, previewKind]);

  const share = async (): Promise<void> => {
    if (!shareable) return;
    setShareError(null);
    try {
      await shareFile(shareable);
    } catch (e) {
      setShareError(`Could not share the file - ${e instanceof Error ? e.message : 'the phone refused'}`);
    }
  };

  /**
   * The one control that gets him the file itself, drawn in the bar and (for a file that cannot be
   * shown) in the body. Three states on a phone, one at a desk - see the header.
   */
  const viaSheet = sheet && previewKind !== 'pdf' && !fileFailed;
  const fileButton = (downloadLabel: string, bar: boolean) => {
    const common = bar ? { variant: 'outline' as const, size: 'sm' as const } : { className: 'mt-4' };
    if (!viaSheet) return <Button {...common} onClick={onDownload}>{downloadLabel}</Button>;
    if (!shareable) return <Button {...common} disabled>Getting the file...</Button>;
    return <Button {...common} onClick={() => void share()}>Share or save</Button>;
  };

  // Escape closes, and focus lands somewhere sane rather than wherever the click left it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const body = (() => {
    if (loading) return <div className="p-10 text-center text-sm text-gray-500">Opening {title}...</div>;
    if (error) return <div className="p-10 text-center text-sm text-red-600">{error}</div>;
    if (!preview) return null;

    if (preview.kind === 'pdf') {
      // The browser's own viewer. Nothing passes through the API: the corpus is 138 MB and
      // almost all of it is PDF.
      return <iframe title={title} src={preview.url} className="w-full h-full border-0" />;
    }

    if (preview.kind === 'html' || preview.kind === 'docx') {
      return (
        <iframe
          title={title}
          srcDoc={preview.html}
          sandbox=""
          className="w-full h-full border-0 bg-white"
        />
      );
    }

    return (
      <div className="p-10 text-center">
        <p className="text-sm text-gray-700">{preview.reason}</p>
        {fileButton(`Download ${preview.filename}`, false)}
      </div>
    );
  })();

  const approximate = preview?.kind === 'docx';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1200] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${title}`}
    >
      <div
        className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <span className="font-semibold truncate flex-1" title={title}>{title}</span>
          {fileButton('Download', true)}
          <button
            ref={closeRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 px-2 text-xl leading-none"
            aria-label="Close preview"
          >
            &times;
          </button>
        </div>

        {notice && (
          <div role="alert" className="px-4 py-2 border-b border-red-200 bg-red-50 text-xs text-red-700">
            {notice}
          </div>
        )}

        {approximate && (
          /**
           * ⭐⭐ NAME THE LOSS, DO NOT JUST FLAG ONE. "Formatting is approximate" sends him
           * hunting for what moved. Measured on the real Beaumont LOI: the body, the 12-row
           * two-column terms table and BOTH signature images all survive conversion; what is
           * missing is the page header and footer, so the KW letterhead and the Apex mark are
           * not here. A diagnostic render that looks like a bug is its own hazard - he reads
           * the picture first and concludes the LETTER is broken.
           */
          <div className="px-4 py-2 bg-warning-wash border-b border-warning/30 text-xs text-warning">
            <strong>Converted preview.</strong> The page header and footer are not shown, so the
            KW letterhead and the Apex mark are missing here. The body, tables and signatures are
            the real ones. {viaSheet ? 'Share or save' : 'Download'} the Word file for the document
            exactly as it will be sent.
          </div>
        )}

        {shareError && (
          <div role="alert" className="px-4 py-2 border-b border-red-200 bg-red-50 text-xs text-red-700">
            {shareError}
          </div>
        )}

        <div className="flex-1 min-h-0 bg-gray-100">{body}</div>
      </div>
    </div>
  );
}
